/** @documentation
 * https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
 * subject to change
 * */

import { Address, LatLng } from '../generated/gql-types';

interface GeocoderAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export type AddressComponent =
  | 'street_address'
  | 'route'
  | 'intersection'
  | 'political'
  | 'country'
  | 'administrative_area_level_1'
  | 'administrative_area_level_2'
  | 'administrative_area_level_3'
  | 'administrative_area_level_4'
  | 'administrative_area_level_5'
  | 'colloquial_area'
  | 'locality'
  | 'sublocality'
  | 'neighborhood'
  | 'premise'
  | 'subpremise'
  | 'plus_code'
  | 'postal_code'
  | 'natural_feature'
  | 'airport'
  | 'park'
  | 'point_of_interest'
  | 'floor'
  | 'establishment'
  | 'landmark'
  | 'parking'
  | 'post_box'
  | 'postal_town'
  | 'room'
  | 'street_number'
  | 'bus_station'
  | 'train_station'
  | 'transit_station';

export type GooglePlaceLike = {
  address_components?: GeocoderAddressComponent[];
  formatted_address?: string;
  name?: string;
  place_id?: string;
  geometry?: {
    location?: {
      lat?: number | (() => number);
      lng?: number | (() => number);
    };
  };
};

export const getAddressComponent = (
  components: GeocoderAddressComponent[],
  filter: AddressComponent,
): GeocoderAddressComponent | undefined => {
  const matches = components.filter((component: GeocoderAddressComponent) =>
    component?.types.find((type: string) => type === filter),
  );

  return matches ? matches[0] : undefined;
};

export const getAddressObject = (
  components: GeocoderAddressComponent[],
  options?: { formattedAddress?: string },
): Address => {
  const subpremise =
    getAddressComponent(components, 'subpremise')?.short_name || '';
  const streetNumber =
    getAddressComponent(components, 'street_number')?.short_name || '';
  const route =
    getAddressComponent(components, 'route')?.short_name ||
    getAddressComponent(components, 'route')?.long_name ||
    '';

  const streetNumberWithUnit = subpremise
    ? `${subpremise}/${streetNumber}`
    : streetNumber;

  let addressLine1 = [streetNumberWithUnit, route]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (!addressLine1) {
    addressLine1 =
      getAddressComponent(components, 'premise')?.long_name ||
      getAddressComponent(components, 'establishment')?.long_name ||
      options?.formattedAddress?.split(',')[0]?.trim() ||
      '';
  }

  const suburb =
    getAddressComponent(components, 'locality')?.short_name ||
    getAddressComponent(components, 'sublocality')?.short_name ||
    getAddressComponent(components, 'postal_town')?.short_name ||
    '';

  return {
    addressLine1,
    addressLine2: '',
    suburb,
    state:
      getAddressComponent(components, 'administrative_area_level_1')
        ?.short_name || '',
    countryLabel: getAddressComponent(components, 'country')?.long_name || '',
    countryValue: getAddressComponent(components, 'country')?.short_name || '',
    postcode: getAddressComponent(components, 'postal_code')?.long_name || '',
    addressFriendly: options?.formattedAddress || undefined,
  };
};

export const buildFriendlyAddressFromParts = (addr: Address): string => {
  const parts = [
    addr.addressLine1,
    addr.addressLine2,
    addr.suburb,
    addr.state,
    addr.postcode,
    addr.countryLabel,
  ].filter(Boolean);

  return parts.join(', ');
};

export const generateFriendlyAddress = (addr: Address): string => {
  if (addr.addressFriendly) {
    return addr.addressFriendly;
  }

  return buildFriendlyAddressFromParts(addr);
};

export const extractLatLngFromPlace = (
  place: GooglePlaceLike,
): LatLng | null => {
  const location = place?.geometry?.location;
  if (!location) {
    return null;
  }

  const lat =
    typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng =
    typeof location.lng === 'function' ? location.lng() : location.lng;

  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return null;
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  if (lat === 0 && lng === 0) {
    return null;
  }

  return { lat, lng };
};

export const buildAddressFromPlace = (
  place: GooglePlaceLike,
): { address: Address; latLng: LatLng | null } => {
  const parsed = getAddressObject(place.address_components || [], {
    formattedAddress: place.formatted_address,
  });

  const address: Address = {
    ...parsed,
    addressFriendly:
      place.formatted_address ||
      parsed.addressFriendly ||
      generateFriendlyAddress(parsed),
  };

  return { address, latLng: extractLatLngFromPlace(place) };
};
